import { DEFAULT_OPUS_PACKAGE_CONFIG } from '../websocketCallService/consts';
import { getHeapViews } from './helpers';
import OpusModuleFactory from './opus_codec.js';
export interface OpusEncoder {
  /**
   * Encode PCM samples (signed 16-bit)
   * @param pcm Int16Array of PCM data
   * @returns Encoded Opus frame (Uint8Array) or null if error
   */
  encode(pcm: Int16Array): Uint8Array | null;

  /**
   * Destroy encoder instance and free memory
   */
  destroy(): void;
}

export interface OpusDecoder {
  /**
   * Decode Opus frame to PCM (signed 16-bit)
   * @param opus Encoded Opus frame
   * @returns Decoded PCM samples or null if error
   */
  decode(opus: Uint8Array): Int16Array | null;

  /**
   * Destroy decoder instance and free memory
   */
  destroy(): void;
}

export interface OpusAPI {
  /**
   * Create Opus encoder
   * @param sampleRate Sampling rate in Hz (e.g. 48000)
   * @param channels Number of channels (1 = mono, 2 = stereo)
   * @param application Opus application mode (e.g. 2048 = OPUS_APPLICATION_AUDIO)
   */
  createEncoder(sampleRate: number, channels: number, application: number): OpusEncoder;

  /**
   * Create Opus decoder
   * @param sampleRate Sampling rate in Hz
   * @param channels Number of channels
   */
  createDecoder(sampleRate: number, channels: number): OpusDecoder;
}

export async function initOpus(): Promise<OpusAPI> {
  const Module: any = await OpusModuleFactory({
    locateFile: (p: string) =>
      p.endsWith('.wasm') ? new URL('./opus_codec.wasm', import.meta.url).toString() : p,
  });

  if (Module.ready) await Module.ready;

  function createEncoder(sampleRate: number, channels: number, application: number) {
    const { U8 } = getHeapViews(Module);

    const errPtr = Module._malloc(4);
    const encoder = Module._opus_encoder_create(sampleRate, channels, application, errPtr);

    return {
      encode: (pcm: Int16Array) => {
        const pcmPtr = Module._malloc(pcm.length * 2);

        const pcmBytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
        U8.set(pcmBytes, pcmPtr);

        const maxPacketSize = 4000;
        const outPtr = Module._malloc(maxPacketSize);
        const length = Module._opus_encode(
          encoder,
          pcmPtr,
          pcm.length / channels,
          outPtr,
          maxPacketSize,
        );

        if (length < 0) {
          console.error('Opus encode error', length);
          Module._free(pcmPtr);
          Module._free(outPtr);
          return null;
        }

        const packet = new Uint8Array(length);
        packet.set(U8.subarray(outPtr, outPtr + length));

        Module._free(pcmPtr);
        Module._free(outPtr);
        return packet;
      },
      destroy: () => Module._opus_encoder_destroy(encoder),
    };
  }

  function createDecoder(sampleRate: number, channels: number) {
    const { U8, I16 } = getHeapViews(Module);

    const errPtr = Module._malloc(4);
    const decoder = Module._opus_decoder_create(sampleRate, channels, errPtr);

    return {
      decode: (opus: Uint8Array) => {
        const opusPtr = Module._malloc(opus.length);
        U8.set(opus, opusPtr);

        const frameSize = DEFAULT_OPUS_PACKAGE_CONFIG.frameSize;
        const pcmPtr = Module._malloc(frameSize * channels * 2);
        const samples = Module._opus_decode(decoder, opusPtr, opus.length, pcmPtr, frameSize, 0);

        if (samples < 0) {
          console.error('Opus decode error', samples);
          Module._free(opusPtr);
          Module._free(pcmPtr);
          return null;
        }

        const pcm = new Int16Array(samples * channels);
        pcm.set(I16.subarray(pcmPtr >> 1, (pcmPtr >> 1) + samples * channels));

        Module._free(opusPtr);
        Module._free(pcmPtr);
        return pcm;
      },
      destroy: () => Module._opus_decoder_destroy(decoder),
    };
  }

  return { createEncoder, createDecoder };
}
